import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Save, Mail, CheckCircle, XCircle, Image, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WelcomeMessage = () => {
  const [settings, setSettings] = useState({
    welcome_enabled: false,
    welcome_subject: 'Welcome to the Newsletter!',
    welcome_body: '<p>Thank you for subscribing to my newsletter!</p><p>You\'ll receive updates about new releases, exclusive content, and more.</p><p>Best regards,<br>Heeloly Upasani</p>'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`);
      setSettings(prev => ({
        ...prev,
        welcome_enabled: response.data.welcome_enabled || false,
        welcome_subject: response.data.welcome_subject || prev.welcome_subject,
        welcome_body: response.data.welcome_body || prev.welcome_body
      }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-settings`, settings);
      toast.success('Welcome message settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, WEBP)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData);
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      
      // Insert image tag at cursor or end
      const imgTag = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;
      setSettings(prev => ({
        ...prev,
        welcome_body: prev.welcome_body + '\n' + imgTag
      }));
      toast.success('Image uploaded! Tag added to email body.');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const insertImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      const imgTag = `<img src="${url}" alt="Image" style="max-width: 100%; height: auto;" />`;
      setSettings(prev => ({
        ...prev,
        welcome_body: prev.welcome_body + '\n' + imgTag
      }));
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  return (
    <div className="admin-section">
      <h2 className="section-subtitle" style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Mail size={24} />
        Welcome Message
      </h2>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>
        Configure the automatic welcome email sent to new newsletter subscribers.
      </p>

      <div className="admin-form">
        <form onSubmit={handleSave}>
          {/* Enable Toggle */}
          <div className="form-group" style={{ marginBottom: '30px', padding: '20px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Label style={{ fontSize: '1.1rem' }}>Enable Welcome Email</Label>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '5px' }}>
                  Automatically send a welcome email when someone subscribes
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.welcome_enabled}
                  onChange={(e) => setSettings({ ...settings, welcome_enabled: e.target.checked })}
                  style={{ width: '20px', height: '20px', marginRight: '10px' }}
                />
                {settings.welcome_enabled ? (
                  <CheckCircle size={24} style={{ color: 'var(--accent-gold)' }} />
                ) : (
                  <XCircle size={24} style={{ color: 'var(--text-muted)' }} />
                )}
              </label>
            </div>
          </div>

          {/* Subject */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <Label>Email Subject</Label>
            <Input
              value={settings.welcome_subject}
              onChange={(e) => setSettings({ ...settings, welcome_subject: e.target.value })}
              placeholder="Welcome to the Newsletter!"
            />
          </div>

          {/* Image Upload Helper */}
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <Label>Add Images to Email</Label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <label style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '8px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem'
              }}>
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
              <Button type="button" onClick={insertImageUrl} className="btn-secondary" style={{ padding: '8px 16px' }}>
                <Image size={16} style={{ marginRight: '8px' }} />
                Insert URL
              </Button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px' }}>
              Upload an image or paste a URL to add images to your email
            </p>
          </div>

          {/* Body */}
          <div className="form-group" style={{ marginBottom: '30px' }}>
            <Label>Email Body (HTML)</Label>
            <Textarea
              value={settings.welcome_body}
              onChange={(e) => setSettings({ ...settings, welcome_body: e.target.value })}
              placeholder="<p>Thank you for subscribing!</p>"
              rows={12}
              style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
            />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px' }}>
              Use HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, &lt;img src=""&gt;, etc.
            </p>
          </div>

          {/* Preview */}
          <div style={{ marginBottom: '30px' }}>
            <Label>Preview</Label>
            <div style={{
              marginTop: '10px',
              padding: '20px',
              background: '#1a1a1a',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontFamily: 'Georgia, serif',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <div style={{ borderBottom: '1px solid #333', paddingBottom: '15px', marginBottom: '15px' }}>
                <strong style={{ color: '#c0c0c0' }}>Subject:</strong> {settings.welcome_subject}
              </div>
              <div dangerouslySetInnerHTML={{ __html: settings.welcome_body }} />
              <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                <p>You received this email because you subscribed to our newsletter.</p>
                <p><a href="#" style={{ color: '#d4af37' }}>Unsubscribe</a></p>
              </div>
            </div>
          </div>

          <Button type="submit" className="btn-primary" disabled={saving}>
            <Save size={18} style={{ marginRight: '8px' }} />
            {saving ? 'Saving...' : 'Save Welcome Message'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default WelcomeMessage;
